import DIContainer from '@components/DependencyInjectionContainer.js';
import { getID } from '@components/Utility/GeneralUtil.js';
import { TOKEN } from '@components/Environment.js';
import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
import { htmlToPlaintext } from '@components/Utility/StringUtil.js';
/**
 * Connects DOM elements to corresponding components
 * @param parentElement - The parent HTML element under which components will be searched for
 * @param components - An object with keys as component IDs and values as component instances
 */
export function connectDOM(parentElement, components) {
    // Query all elements with a data-componentid attribute
    const elements = parentElement.querySelectorAll('[data-componentid]');
    // Loop over the NodeList
    elements.forEach(element => {
        const componentID = element.getAttribute('data-componentid');
        if (components[componentID]) {
            components[componentID].rootElement = element;
            components[componentID].draw();
        }
    });
}
/**
 * Creates component instances from DOM elements.
 * For each DOM element with a 'data-componenttype' attribute, creates a new instance of the corresponding component type
 * and associates it with the DOM element. Component types are looked up in the global 's360' object.
 * @param parentElement - The parent HTML element under which component DOM elements will be searched for
 * @returns An object with keys as component IDs and values as component instances
 */
export function createComponentsFromDOM(parentElement) {
    const components = {};
    const s360 = window.s360;
    if (s360) {
        const elements = parentElement.querySelectorAll('[data-componenttype]');
        elements.forEach((element) => {
            const className = element.dataset.componenttype;
            if (className) {
                const id = element.dataset.componentid || getID(className);
                try {
                    const component = DIContainer.resolve(className, id, element);
                    component.rootElement = element;
                    components[id] = component;
                    component.draw();
                }
                catch (e) {
                    console.log(e);
                }
            }
        });
    }
    return components;
}
function parseConditionals(template, options, tokenValues) {
    if (!options.enableConditionals || template.indexOf("{{#if") === -1) {
        return template;
    }
    // Matches: {{#if token:modifier=compareWith}} or {{#elseif token:modifier=compareWith}}
    const parseCondTag = (tag) => {
        const m = /^{{#(?:if|elseif)\s+([^\s:=}]+)(?::([^\s=}:]+))?(?:=([^\s}]+))?}}$/.exec(tag.trim());
        if (!m)
            return null;
        return { tokenName: m[1], modifier: m[2], compareWith: m[3] };
    };
    const evalCondition = (tokenName, modifier, compareWith) => {
        let tokenValue = "";
        // TODO: this logic needs to better match the way we handle the tokens when actually parsing the template as well.
        if (tokenValues && Reflect.has(tokenValues, tokenName)) {
            tokenValue = tokenValues[tokenName];
        }
        else if (options.parseTokenCallback) {
            tokenValue = options.parseTokenCallback(tokenName, modifier || "") ?? "";
        }
        if (modifier)
            tokenValue = formatToken(tokenValue, modifier);
        if (compareWith !== undefined && compareWith !== null) {
            return tokenValue === compareWith;
        }
        return tokenValue !== "" && tokenValue !== null && tokenValue !== undefined;
    };
    const OPEN_IF = "{{#if";
    const OPEN_ELSEIF = "{{#elseif";
    const ELSE = "{{#else}}";
    const CLOSE = "{{/if}}";
    // Reduce innermost blocks repeatedly
    let firstOpen = template.indexOf(OPEN_IF);
    while (firstOpen > -1) {
        firstOpen = template.indexOf(OPEN_IF);
        if (firstOpen === -1)
            break;
        // Find innermost {{#if ...}} ... {{/if}} using a stack
        let i = 0;
        const stack = [];
        let foundPair = null;
        while (i < template.length) {
            const nextOpen = template.indexOf(OPEN_IF, i);
            const nextClose = template.indexOf(CLOSE, i);
            if (nextOpen !== -1 && (nextOpen < nextClose || nextClose === -1)) {
                const tagEnd = template.indexOf("}}", nextOpen);
                if (tagEnd === -1)
                    break; // malformed
                stack.push({ openStart: nextOpen, openEnd: tagEnd + 2 });
                i = tagEnd + 2;
            }
            else if (nextClose !== -1) {
                const opener = stack.pop();
                if (!opener) {
                    // Unbalanced; skip this close and continue
                    i = nextClose + CLOSE.length;
                    continue;
                }
                foundPair = {
                    openStart: opener.openStart,
                    openEnd: opener.openEnd,
                    closeStart: nextClose,
                    closeEnd: nextClose + CLOSE.length
                };
                break; // innermost found
            }
            else {
                break;
            }
        }
        if (!foundPair)
            break;
        const { openStart, openEnd, closeStart, closeEnd } = foundPair;
        // Parse head {{#if ...}}
        const openTag = template.slice(openStart, openEnd);
        const head = parseCondTag(openTag);
        if (!head) {
            // malformed opener; drop the tag to avoid infinite loop
            template = template.slice(0, openStart) + template.slice(openEnd);
            continue;
        }
        // Inner content of this block (between head and close)
        const inner = template.slice(openEnd, closeStart);
        const segments = [{ cond: head, content: "" }];
        let elseContent = "";
        let cursor = 0;
        let hitElse = false;
        const findNextMarker = (from) => {
            const idxElseIf = inner.indexOf(OPEN_ELSEIF, from);
            const idxElse = inner.indexOf(ELSE, from);
            if (idxElseIf === -1 && idxElse === -1)
                return { type: null, idx: -1 };
            if (idxElseIf !== -1 && (idxElseIf < idxElse || idxElse === -1)) {
                return { type: "elseif", idx: idxElseIf };
            }
            return { type: "else", idx: idxElse };
        };
        while (cursor <= inner.length) {
            const { type, idx } = findNextMarker(cursor);
            if (!type) {
                // No more markers
                if (hitElse)
                    elseContent += inner.slice(cursor);
                else
                    segments[segments.length - 1].content += inner.slice(cursor);
                break;
            }
            if (type === "elseif") {
                // Content up to elseif belongs to current segment
                segments[segments.length - 1].content += inner.slice(cursor, idx);
                // Read elseif tag
                const tagEnd = inner.indexOf("}}", idx);
                if (tagEnd === -1)
                    break; // malformed
                const elseifTag = inner.slice(idx, tagEnd + 2);
                const parsedElseIf = parseCondTag(elseifTag);
                segments.push({ cond: parsedElseIf ?? undefined, content: "" });
                cursor = tagEnd + 2;
            }
            else {
                // ELSE
                segments[segments.length - 1].content += inner.slice(cursor, idx);
                cursor = idx + ELSE.length;
                hitElse = true;
                elseContent = inner.slice(cursor); // rest belongs to else
                break;
            }
        }
        // Evaluate segments in order (first truthy wins), otherwise else (if present)
        let replacement = "";
        const winner = segments.find(s => s.cond && evalCondition(s.cond.tokenName, s.cond.modifier, s.cond.compareWith));
        if (winner)
            replacement = winner.content;
        else if (hitElse)
            replacement = elseContent;
        // Replace whole block
        template = template.slice(0, openStart) + replacement + template.slice(closeEnd);
    }
    return template;
}
/**
 * Replaces tokens in a template string with values from tokenValues.
 * template format: 'My Branch is {{branch}}'
 * tokenValues format: {branch: 'HEADOFFICE'}
 * @param {string} template - The template string containing tokens to be replaced.
 * @param {Object} tokenValues - An object containing key-value pairs where the keys represent tokens and the values represent their replacements.
 * @param options
 * @returns {string} - The updated template string with tokens replaced by their respective values.
 */
export function replaceTokens(template, tokenValues, options = {}) {
    // Conditional processing is optional; enabled only when needed.
    // if (options.enableConditionals && template.indexOf('{{#if') > -1) {
    // updated to support nested {{#if}} blocks
    template = parseConditionals(template, options, tokenValues);
    // Matches conditional blocks like:
    //   {{#if tokenName:modifier=compareWith}} ... true block ... {{#else}} ... false block ... {{/if}}
    // or with only token and optional modifier:
    //   {{#if tokenName:modifier}} ... true block ... {{/if}}
    // const conditionalRegex = /{{#if\s+([^:}]+)(?::([^=}]+))?(?:=([^}]+))?}}([\s\S]*?)(?:{{#else}}([\s\S]*?))?{{\/if}}/g;
    /*const conditionalRegex = /{{#if\s+([^\s:=}]+)(?::([^\s=}:]+))?(?:=([^\s}]+))?}}([\s\S]*?)(?:{{#else}}([\s\S]*?))?{{\/if}}/g;
    template = template.replace(conditionalRegex, (match, tokenName, modifier, compareWith, ifContent, elseContent) => {
        let tokenValue = '';

        // Retrieve token's value: try parseTokenCallback first, then tokenValues.
        if (options.parseTokenCallback) {
            tokenValue = options.parseTokenCallback(tokenName, modifier) || '';
        } else if (tokenValues && Reflect.has(tokenValues, tokenName)) {
            tokenValue = tokenValues[tokenName];
        }

        // If a modifier is provided, format the token value.
        if (modifier) {
            tokenValue = formatToken(tokenValue, modifier);
        }

        // Evaluate the condition.
        let conditionSatisfied = false;
        // If a compare value is provided, require an exact match.
        if (typeof compareWith !== 'undefined' && compareWith !== null) {
            conditionSatisfied = tokenValue === compareWith;
        } else {
            // Otherwise, any non-empty value is truthy.
            conditionSatisfied = tokenValue !== '' && tokenValue !== null && tokenValue !== undefined;
        }
        return conditionSatisfied ? ifContent : (elseContent || '');
    });*/
    // }
    /*const process = (tokenValues: {[token: string]: string}, prefix: string = '') => {
        if (template.indexOf('{{') === -1) {
            return;
        }
        
        for (let key in tokenValues) {
            const regexKey = prefix ? `${prefix}.${key}` : key;
            let tokenValue = tokenValues[key] || '';
            // Create a regex to match the token format
            // let regex = new RegExp(`{{${regexKey}}}`, 'g');
            let regex = new RegExp(`{{(${regexKey})(?::([a-zA-Z]+))?}}`, 'g');
            
            template = template.replace(regex, (match, token, modifier) => {
                let value = tokenValue;
                if (modifier) {
                    if (modifier === 'tokenLiteral') {
                        return match;
                    }
                    value = formatToken(tokenValue, modifier);
                }
                // URL encode the value if required by options
                if (options.urlEncode) {
                    value = encodeURIComponent(value);
                }
                return value;
            });
            // if (options.urlEncode) {
            // 	template = template.replace(regex, encodeURIComponent(tokenValue));
            // } else {
            // 	template = template.replace(regex, tokenValue);
            // }
        }
    }
    if (tokenValues) {
        process(tokenValues, '');
    }
    if (options.records && template.indexOf('{{') > -1) {
        forEachKey(options.records, (record, key) => {
            process(record.data, key);
        })
    }*/
    // Check if there are any potential global tokens before using regex
    if (template.indexOf('{{') > -1) {
        // Use a single regex pass to match all tokens of the form {{...}}.
        // This regex splits the token into its "name" and optional modifier parts.
        const tokenRegex = /{{([^}]+)}}/g;
        template = template.replace(tokenRegex, (match, tokenTag) => {
            // Split tokenTag by ":" to extract an optional modifier.
            const [tokenNamePart, modifier] = tokenTag.split(':');
            let tokenValue;
            // 1. Prioritize tokenValues if provided.
            if (!options.recordTokensOnly && tokenValues && Reflect.has(tokenValues, tokenNamePart)) {
                tokenValue = tokenValues[tokenNamePart] || ''; // handle null so we dont get 'null'
            }
            else if (typeof tokenValue === 'undefined' && options.records) {
                // 2. If not found, and if a records map is provided,
                // check if the token follows the record.field pattern.
                const dotIndex = tokenNamePart.indexOf('.');
                if (dotIndex > -1) {
                    const recordName = tokenNamePart.substring(0, dotIndex);
                    const fieldName = tokenNamePart.substring(dotIndex + 1);
                    const record = options.records[recordName];
                    if (record && Reflect.has(record.data, fieldName)) {
                        tokenValue = record.data[fieldName] || '';
                    }
                }
            }
            // 3. Check for Global tokens
            if (!options.recordTokensOnly && typeof tokenValue === 'undefined') {
                tokenValue = TOKEN(`{{${tokenNamePart}}}`);
            }
            // 4. If still undefined, check the parseTokenCallback.
            if (typeof tokenValue === 'undefined' && options.parseTokenCallback) {
                tokenValue = options.parseTokenCallback(tokenNamePart, modifier || '');
            }
            // 5. If still undefined, and we're not keeping missing tokens, default to empty string.
            if (typeof tokenValue === 'undefined' && !options.keepMissingTokens) {
                tokenValue = '';
            }
            // 6. If a modifier is present, format the token.
            if (tokenValue !== undefined && modifier) {
                if (modifier === 'tokenLiteral') {
                    tokenValue = tokenNamePart;
                }
                tokenValue = formatToken(tokenValue, modifier);
            }
            // 6. URL encode if required.
            if (tokenValue !== undefined && options.urlEncode && modifier !== 'encoded') {
                tokenValue = encodeURIComponent(tokenValue);
            }
            return tokenValue !== undefined ? tokenValue : match;
        });
        // const globalTokenRegex = /{{(.*?)}}/g;
        // const matches = [...template.matchAll(globalTokenRegex)];
        // matches.forEach(match => {
        // 	const tag = match[1];
        // 	const [tokenName, modifier] = tag.split(':');
        //
        // 	let tokenValue: string|undefined;
        // 	if (options.parseTokenCallback) {
        // 		tokenValue = options.parseTokenCallback(tokenName, modifier);
        // 	}
        // 	if (modifier === 'tokenLiteral'){
        // 		tokenValue = tokenName;
        // 	}
        // 	if (typeof tokenValue === 'undefined') {
        // 		tokenValue = TOKEN(`{{${tokenName}}}`);
        // 	}
        // 	if (typeof tokenValue === 'undefined' && !options.keepMissingTokens) {
        // 		tokenValue = '';
        // 	}
        // 	if (typeof tokenValue !== 'undefined') {
        // 		tokenValue = formatToken(tokenValue, modifier);
        //
        // 		if (options.urlEncode) {
        // 			tokenValue = encodeURIComponent(tokenValue as string);
        // 		}
        // 		template = template.replace(`{{${tag}}}`, tokenValue as string);
        // 	} else if (!options.keepMissingTokens) {
        // 		template = template.replace(`{{${tag}}}`, '');
        // 	}
        // })
    }
    return template;
}
export function formatToken(tokenValue, modifier) {
    if (tokenValue) {
        switch (modifier) {
            case 'dateFormat':
                tokenValue = DateTimeManager.formatDate(tokenValue, 'display');
                break;
            case 'datetimeFormat':
                tokenValue = DateTimeManager.formatDateTime(tokenValue, 'display');
                break;
            case 'plaintext':
                tokenValue = htmlToPlaintext(tokenValue);
                break;
            case 'tokenLiteral':
                tokenValue = `{{${tokenValue}}}`;
                break;
            case 'tokenEncoded':
                tokenValue = encodeURIComponent(tokenValue);
                break;
        }
    }
    return tokenValue;
}
export function createElement(tag, classNames = [], attributes = {}) {
    const element = document.createElement(tag);
    element.classList.add(...classNames);
    for (const [key, value] of Object.entries(attributes)) {
        element.setAttribute(key, value);
    }
    return element;
}
